import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  Sparkles,
  Heart,
  ArrowRight,
  CheckCircle2,
  Eye,
  MessageCircle,
  Clock,
  Image,
  Calendar,
  MapPin,
  Bell,
  Check,
  Users,
  Star,
  Video,
  Mic,
  BookOpen,
  GraduationCap,
  Presentation,
  Target,
  Award,
  TrendingUp,
  Lightbulb,
  MessageSquare,
  Share,
  Zap,
  Play,
  FileText,
  Layers
} from "lucide-react";

const InstructorSalesPage = () => {
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [selectedType, setSelectedType] = useState("workshop");
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Instructors & Educators";
  }, []);

  // Instructor types
  const instructorTypes = {
    workshop: {
      name: "Workshop Leaders",
      icon: Presentation,
      iconColor: "text-purple-500",
      description: "Teaching hands-on skills to groups",
      examples: ["Beauty educators", "Craft instructors", "Culinary classes", "DIY workshops"],
      features: [
        "Class capacity management",
        "Skill level matching",
        "Material lists & prep guides",
        "Progress tracking per student"
      ],
      studentPreferences: [
        { label: "Experience level", options: ["Complete beginner", "Some experience", "Intermediate", "Advanced"] },
        { label: "Learning style", options: ["Visual", "Hands-on", "Read/write", "Listen"] },
        { label: "Class format", options: ["In-person", "Virtual", "Hybrid"] },
        { label: "Goals", options: ["Hobby", "Career change", "Skill upgrade", "Certification"] }
      ]
    },
    oneOnOne: {
      name: "1-on-1 Instructors",
      icon: Users,
      iconColor: "text-blue-500",
      description: "Private lessons & personalized training",
      examples: ["Beauty mentors", "Fitness coaches", "Music teachers", "Language tutors"],
      features: [
        "Custom lesson plans",
        "Progress milestones",
        "Flexible scheduling",
        "Session notes & feedback"
      ],
      studentPreferences: [
        { label: "Current skill level", options: ["Beginner", "Intermediate", "Advanced", "Professional"] },
        { label: "Pace preference", options: ["Slow & thorough", "Moderate", "Fast-paced", "Flexible"] },
        { label: "Feedback style", options: ["Gentle encouragement", "Direct & honest", "Mix of both"] },
        { label: "Session frequency", options: ["Weekly", "Bi-weekly", "Monthly", "Intensive (multiple/week)"] }
      ]
    },
    speaker: {
      name: "Speakers & Presenters",
      icon: Mic,
      iconColor: "text-amber-500",
      description: "Events, conferences, and talks",
      examples: ["Industry experts", "Motivational speakers", "Panel moderators", "Conference presenters"],
      features: [
        "Event integration",
        "Audience engagement tools",
        "Q&A management",
        "Feedback collection"
      ],
      audiencePreferences: [
        { label: "Audience size", options: ["Intimate (<20)", "Medium (20-100)", "Large (100+)", "Virtual any size"] },
        { label: "Presentation style", options: ["Interactive", "Lecture format", "Workshop hybrid", "Panel discussion"] },
        { label: "Tech requirements", options: ["Basic slides", "Demo/live coding", "A/V heavy", "Minimal tech"] },
        { label: "Duration", options: ["15-30 min", "45-60 min", "Half day", "Full day"] }
      ]
    },
    community: {
      name: "Community Educators",
      icon: Heart,
      iconColor: "text-pink-500",
      description: "Ongoing programs & memberships",
      examples: ["Subscription communities", "Ongoing cohorts", "Mastermind groups", "Membership programs"],
      features: [
        "Member onboarding",
        "Content scheduling",
        "Community engagement",
        "Renewal management"
      ],
      memberPreferences: [
        { label: "Engagement level", options: ["Active participant", "Observer/lurker", "Occasional contributor"] },
        { label: "Content format", options: ["Live sessions", "Pre-recorded", "Written/text", "Mix of all"] },
        { label: "Community style", options: ["High interaction", "Low pressure", "Accountability focused"] },
        { label: "Time commitment", options: ["< 1 hr/week", "1-3 hrs/week", "3+ hrs/week", "Flexible"] }
      ]
    }
  };

  const currentType = instructorTypes[selectedType];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
              <GraduationCap className="w-3 h-3 mr-1" />
              Instructor
            </Badge>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="join-pilot-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <EditableSection sectionId="instructor-hero" label="Instructor Hero" className={editMode ? "relative overflow-hidden py-20 px-4" : "relative overflow-hidden py-20 px-4"}>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-64 h-64 bg-purple-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-indigo-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-white/80 text-purple-700 border border-purple-200">
            <Sparkles className="w-3 h-3 mr-1" />
            <EditableText sectionId="instructor-hero" field="badge" defaultValue="For Educators, Workshop Leaders & Speakers" className="inline" />
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-purple-600 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
              <EditableText sectionId="instructor-hero" field="title_highlight" defaultValue="teaching" className="inline" />
            </span>{" "}
            <EditableText sectionId="instructor-hero" field="title_line1" defaultValue="preferences," className="inline" />
            <br />
            <EditableText sectionId="instructor-hero" field="title_line2" defaultValue="perfectly communicated." className="inline" />
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            <EditableText 
              sectionId="instructor-hero" 
              field="subtitle" 
              defaultValue="Know your students' learning styles, skill levels, and goals before the first session. Transform how you teach."
              multiline
            />
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              size="lg"
              className="bg-purple-600 hover:bg-purple-700 text-white h-12 px-8 rounded-full"
              data-testid="hero-join-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-purple-300 text-purple-700 hover:bg-purple-50 h-12 px-8 rounded-full"
            >
              See How It Works
            </Button>
          </div>
        </div>
      </EditableSection>

      {/* Instructor Types */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            Built for every type of educator
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Whether you teach 1-on-1 or to thousands, phygital adapts to your teaching style.
          </p>
          
          {/* Type Selector */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {Object.entries(instructorTypes).map(([key, type]) => (
              <Button
                key={key}
                variant={selectedType === key ? "default" : "outline"}
                className={`rounded-full ${selectedType === key ? 'bg-purple-600 hover:bg-purple-700' : 'hover:bg-purple-50'}`}
                onClick={() => setSelectedType(key)}
                data-testid={`type-${key}`}
              >
                <type.icon className={`w-4 h-4 mr-2 ${selectedType === key ? 'text-white' : type.iconColor}`} />
                {type.name}
              </Button>
            ))}
          </div>
          
          {/* Selected Type Card */}
          <Card className="max-w-3xl mx-auto border-purple-100">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center`}>
                  <currentType.icon className={`w-6 h-6 ${currentType.iconColor}`} />
                </div>
                <div>
                  <CardTitle>{currentType.name}</CardTitle>
                  <p className="text-sm text-gray-600">{currentType.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Examples */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    Perfect for
                  </h4>
                  <ul className="space-y-2">
                    {currentType.examples.map((example, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-purple-500" />
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Features */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                    <Zap className="w-4 h-4 text-purple-500" />
                    Key features
                  </h4>
                  <ul className="space-y-2">
                    {currentType.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Preference Fields */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                  <Heart className="w-4 h-4 text-pink-500" />
                  {selectedType === 'speaker' ? 'Audience' : selectedType === 'community' ? 'Member' : 'Student'} Preference Fields
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(currentType.studentPreferences || currentType.audiencePreferences || currentType.memberPreferences).map((pref, idx) => (
                    <div key={idx} className="bg-purple-50/50 rounded-lg p-3 border border-purple-100">
                      <p className="text-sm font-medium text-gray-700 mb-1">{pref.label}</p>
                      <p className="text-xs text-gray-500">
                        {pref.options.slice(0, 3).join(", ")}{pref.options.length > 3 && "..."}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            How it works
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-xl mx-auto">
            A seamless experience for both educators and learners
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">Set up your profile</h3>
              <p className="text-gray-600 text-sm">
                Define your teaching style, services, and what you need to know about students
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600">2</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">Students share preferences</h3>
              <p className="text-gray-600 text-sm">
                Learners fill out their preference card with learning style, goals, and context
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">Teach with context</h3>
              <p className="text-gray-600 text-sm">
                Start every session knowing exactly how to help them succeed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">
            Why educators love phygital
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-purple-100 hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">Personalized from day one</h3>
                <p className="text-sm text-gray-600">
                  No more guessing. Know their learning style, goals, and challenges before you meet.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-100 hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold mb-2">Track progress</h3>
                <p className="text-sm text-gray-600">
                  Document milestones, share feedback, and celebrate wins together.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-purple-100 hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Build your reputation</h3>
                <p className="text-sm text-gray-600">
                  Students remember educators who truly understand them.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to transform how you teach?
          </h2>
          <p className="text-purple-100 text-lg mb-8">
            Get early access and help us build the future of education.
          </p>
          <Button
            onClick={() => setShowEarlyAccessModal(true)}
            size="lg"
            className="bg-white text-purple-700 hover:bg-purple-50 h-12 px-8 rounded-full font-semibold"
            data-testid="cta-join-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="instructor" /> */}

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccessModal}
        onOpenChange={setShowEarlyAccessModal}
        category="instructor"
      />
    </div>
  );
};

export default InstructorSalesPage;
